import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import moment from 'moment'
import { Layers, LayoutGrid } from 'lucide-react'
import { Text, Number, SelectBox, MultiSelectBox, SelectStatusBox, File, DatePicker, TextArea, Radio, Checkbox } from 'components/input_fields'
import { AddServiceProviderSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { serviceTypeList, serviceSubTypeList } from '../../../../redux/slices/master'
import { premiseList, unitList, removeUnit } from '../../../../redux/slices/premises'
import { addServiceProvider } from '../../../../redux/slices/users'
import { addUpdateHandler, SessionManager } from 'utils'
import { useTranslation } from 'react-i18next'

export default function AddServiceProviderForm() {

    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const serviceType = useSelector(state => state?.serviceType?.list)
    const serviceSubType = useSelector(state => state?.serviceSubType?.list)
    const subTypeLoading = useSelector(state => state?.serviceSubType?.loading)
    const premise = useSelector(state => state?.premise?.list)
    const units = useSelector(state => state?.unit?.list)
    const unitLoading = useSelector(state => state?.unit?.loading)

    const type_option = serviceType?.map(val => (
        { "value": val.id, "label": val.type_name }
    ))

    const sub_type_option = serviceSubType?.map(val => (
        { "value": val.id, "label": val.sub_type_name }
    ))

    const premise_option = premise?.map(val => (
        { "value": val.id, "label": val.name }
    ))

    const unit_option = units?.map(val => ({
        "value": val.id,
        "label": val.unit_number,
        "zone_name": val?.zoneInfo?.zone_name,
        "level_name": val?.levelInfo?.level_name,
    }))

    const unitOptionRender = (option) => (
        <div className="unit-option-row">
            <span className="unit-option-label">
                {option.data.label}
            </span>
            <div className="unit-option-badges">
                {option.data.zone_name && (
                    <span className="unit-option-badge-zone">
                        <Layers size={9} />
                        {option.data.zone_name}
                    </span>
                )}
                {option.data.level_name && (
                    <span className="unit-option-badge-level">
                        <LayoutGrid size={9} />
                        {option.data.level_name}
                    </span>
                )}
            </div>
        </div>
    )

    const id_type_option = [
        { "value": 'Aadhar Card', "label": 'Aadhar Card' },
        { "value": 'Pan Card', "label": 'Pan Card' },
        { "value": 'Driving License', "label": 'Driving License' },
        { "value": 'Voter ID', "label": 'Voter ID' },
    ]

    const user_data = JSON.parse(SessionManager.getCookie("user"))

    const initialValues = {
        premise_id: user_data?.premise_id || '',
        name: '',
        mobile: '',
        type: '',
        sub_type: '',
        validity: moment().format('DD/MM/YYYY'),
        profile: '',
        status: 'Active',

        id_type: '',
        id_number: '',
        upload_document: '',

        present_address: '',
        permanent_address: '',
        same_as_present: false,

        police_verification: 'Not Verified',
        is_hireable: 'Hireable',
        is_frequent_visitor: 'Non Frequent Visitor',
        unit_ids: [],
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues: initialValues,
        validationSchema: AddServiceProviderSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(addServiceProvider({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('serviceProvider.serviceProvider')} ${t('common.success_add')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: RoutesMap.SERVICE_PROVIDER_LIST.path,
                spinner: setSpinner,
            }))
        },
    })

    const handleSameAddress = (e) => {
        const isChecked = e.target.checked
        setFieldValue('same_as_present', isChecked)
        if (isChecked) {
            setFieldValue('permanent_address', values.present_address)
        } else {
            setFieldValue('permanent_address', '')
        }
    }

    useEffect(() => {
        if (values.same_as_present) {
            setFieldValue('permanent_address', values.present_address)
        }
    }, [values.present_address])

    useEffect(() => {
        setLoading(true)
        Promise.all([
            dispatch(serviceTypeList({ navigate })),
            dispatch(premiseList({ navigate })),
        ]).finally(() => setLoading(false));
    }, [])

    useEffect(() => {
        if (values.type) {
            const data = { type_id: values.type };
            dispatch(serviceSubTypeList({ pageable: data, navigate }))
        }
    }, [values.type])

    useEffect(() => {
        if (values.is_hireable !== 'Hireable') {
            setFieldValue('unit_ids', [])
            dispatch(removeUnit())
            return
        }

        if (!values.premise_id) {
            setFieldValue('unit_ids', [])
            dispatch(removeUnit())
            return
        }

        const pageable = { premise_id: values.premise_id }
        dispatch(unitList({ pageable, navigate }))
    }, [values.premise_id, values.is_hireable])

    return (
        <>
            <div className="container-fluid">
                <div className='layout'>
                    {loading ?
                        <div className="row">
                            <div className="col-md-12">
                                <Loader />
                            </div>
                        </div>
                        :
                        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="row">

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('serviceProvider.premise')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={user_data?.premise_id}
                                        option={premise_option}
                                        name='premise_id'
                                        value={values.premise_id}
                                        onChange={(e) => {
                                            setFieldValue('premise_id', e)
                                            setFieldValue('unit_ids', [])
                                        }}
                                        onBlur={() => setFieldTouched("premise_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.premise_id && touched.premise_id ? (<span className='field_error'>{errors.premise_id}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('serviceProvider.name')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='name'
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.name && touched.name ? (<span className='field_error'>{errors.name}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Number
                                        label_name={t('serviceProvider.mobile')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='mobile'
                                        value={values.mobile}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.mobile && touched.mobile ? (<span className='field_error'>{errors.mobile}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('serviceProvider.type')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={type_option}
                                        name='type'
                                        value={values.type}
                                        onChange={(e) => {
                                            setFieldValue('type', e)
                                            setFieldValue('sub_type', '')
                                        }}
                                        onBlur={() => setFieldTouched("type", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.type && touched.type ? (<span className='field_error'>{errors.type}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('serviceProvider.sub_type')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        loading={subTypeLoading}
                                        option={sub_type_option}
                                        name='sub_type'
                                        value={values.sub_type}
                                        onChange={(e) => setFieldValue('sub_type', e)}
                                        onBlur={() => setFieldTouched("sub_type", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.sub_type && touched.sub_type ? (<span className='field_error'>{errors.sub_type}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <DatePicker
                                        label_name={t('serviceProvider.validity')}
                                        mandatory={true}
                                        disabled={false}
                                        name='validity'
                                        minDate={moment().format('DD/MM/YYYY')}
                                        value={values.validity}
                                        onChange={(date, dateString) => setFieldValue('validity', dateString)}
                                        onBlur={() => setFieldTouched("validity", true)}
                                        error={errors.validity && touched.validity ? (<span className='field_error'>{errors.validity}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <File
                                        label_name={t('serviceProvider.profile')}
                                        mandatory={false}
                                        disabled={false}
                                        name='profile'
                                        onChange={(e) => setFieldValue('profile', e.currentTarget.files[0])}
                                        onBlur={handleBlur}
                                        error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                        accept="image/jpg, image/jpeg, image/png"
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectStatusBox
                                        label_name={t('serviceProvider.status')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={true}
                                        name='status'
                                        value={values.status}
                                        onChange={(e) => setFieldValue('status', e)}
                                        onBlur={() => setFieldTouched("status", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.status && touched.status ? (<span className='field_error'>{errors.status}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12 mt-4">
                                    <h6 className="form_label mb-4 pb-2 border-bottom fw-bold text-secondary text-uppercase" style={{ letterSpacing: '1px' }}>
                                        {t('serviceProvider.document')}
                                    </h6>
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('serviceProvider.id_type')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        option={id_type_option}
                                        name='id_type'
                                        value={values.id_type}
                                        onChange={(e) => setFieldValue('id_type', e)}
                                        onBlur={() => setFieldTouched("id_type", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.id_type && touched.id_type ? (<span className='field_error'>{errors.id_type}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('serviceProvider.id_number')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        name='id_number'
                                        value={values.id_number}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.id_number && touched.id_number ? (<span className='field_error'>{errors.id_number}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <File
                                        label_name={t('serviceProvider.upload_document')}
                                        mandatory={false}
                                        disabled={false}
                                        name='upload_document'
                                        onChange={(e) => setFieldValue('upload_document', e.currentTarget.files[0])}
                                        onBlur={handleBlur}
                                        error={errors.upload_document && touched.upload_document ? (<span className='field_error'>{errors.upload_document}</span>) : null}
                                        accept="image/jpg, image/jpeg, image/png, application/pdf"
                                    />
                                </div>

                                <div className="col-md-6 mt-3">
                                    <TextArea
                                        label_name={t('serviceProvider.present_address')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        name='present_address'
                                        value={values.present_address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.present_address && touched.present_address ? (<span className='field_error'>{errors.present_address}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-6 mt-3">
                                    <TextArea
                                        label_name={t('serviceProvider.permanent_address')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={values.same_as_present}
                                        name='permanent_address'
                                        value={values.permanent_address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.permanent_address && touched.permanent_address ? (<span className='field_error'>{errors.permanent_address}</span>) : null}
                                    />
                                    <div className="mt-2">
                                        <Checkbox
                                            label_value={t('serviceProvider.same_as_present')}
                                            check={values.same_as_present}
                                            name="same_as_present"
                                            onChange={handleSameAddress}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-12 mt-4">
                                    <h6 className="form_label mb-4 pb-2 border-bottom fw-bold text-secondary text-uppercase" style={{ letterSpacing: '1px' }}>
                                        {t('serviceProvider.other_details')}
                                    </h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <Form.Group className="mb-2">
                                                <Form.Label className='form_label_red'>{t('serviceProvider.police_verification')}*</Form.Label>
                                                <div className="d-flex gap-3">
                                                    <Radio
                                                        label_value={t('serviceProvider.verified')}
                                                        check={values.police_verification === 'Verified'}
                                                        name="police_verification"
                                                        value="Verified"
                                                        onChange={handleChange}
                                                    />
                                                    <Radio
                                                        label_value={t('serviceProvider.not_verified')}
                                                        check={values.police_verification === 'Not Verified'}
                                                        name="police_verification"
                                                        value="Not Verified"
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </div>

                                        <div className="col-md-4">
                                            <Form.Group className="mb-2">
                                                <Form.Label className='form_label_red'>{t('serviceProvider.is_hireable')}*</Form.Label>
                                                <div className="d-flex gap-3">
                                                    <Radio
                                                        label_value={t('serviceProvider.hireable')}
                                                        check={values.is_hireable === 'Hireable'}
                                                        name="is_hireable"
                                                        value="Hireable"
                                                        onChange={(e) => {
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <Radio
                                                        label_value={t('serviceProvider.not_hireable')}
                                                        check={values.is_hireable === 'Not Hireable'}
                                                        name="is_hireable"
                                                        value="Not Hireable"
                                                        onChange={(e) => {
                                                            handleChange(e)
                                                            setFieldValue('unit_ids', [])
                                                        }}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </div>

                                        <div className="col-md-4">
                                            <Form.Group className="mb-2">
                                                <Form.Label className='form_label_red'>{t('serviceProvider.is_frequent_visitor')}*</Form.Label>
                                                <div className="d-flex gap-3">
                                                    <Radio
                                                        label_value={t('serviceProvider.frequent')}
                                                        check={values.is_frequent_visitor === 'Frequent Visitor'}
                                                        name="is_frequent_visitor"
                                                        value="Frequent Visitor"
                                                        onChange={handleChange}
                                                    />
                                                    <Radio
                                                        label_value={t('serviceProvider.not_frequent')}
                                                        check={values.is_frequent_visitor === 'Non Frequent Visitor'}
                                                        name="is_frequent_visitor"
                                                        value="Non Frequent Visitor"
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {values.is_hireable === 'Hireable' && (
                                    <>
                                        <div className="col-md-12 mt-4">
                                            <h6 className="form_label mb-4 pb-2 border-bottom fw-bold text-secondary text-uppercase" style={{ letterSpacing: '1px' }}>
                                                {t('serviceProvider.unit_details')}
                                            </h6>
                                        </div>

                                        <div className="col-md-6">
                                            <MultiSelectBox
                                                label_name={t('serviceProvider.units')}
                                                mandatory={false}
                                                placeholder=''
                                                disabled={!values.premise_id}
                                                loading={unitLoading}
                                                option={unit_option}
                                                customOption={unitOptionRender}
                                                name='unit_ids'
                                                value={values.unit_ids}
                                                onChange={(e) => setFieldValue('unit_ids', e)}
                                                onBlur={() => setFieldTouched("unit_ids", true)}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                error={errors.unit_ids && touched.unit_ids ? (<span className='field_error'>{errors.unit_ids}</span>) : null}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="col-md-12 mt-4">
                                    <div className="d-flex justify-content-end">
                                        <div className="col-lg-2">
                                            <SaveButton
                                                button_name={t('common.submit')}
                                                spinner={spinner}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </Form>
                    }
                </div>
            </div>
        </>
    )
}